/**
 * Lecture des fichiers GPX, extensions VeloGPX comprises.
 *
 * Portage de `GpxParser.kt`. Deux principes structurent ce fichier :
 *
 *  - **Tronquer, jamais échouer.** Un fichier qui dépasse une limite voit
 *    l'ajout s'arrêter là ; il n'y a pas d'exception. Un GPX abîmé ou
 *    volontairement hostile doit dégrader l'affichage, pas empêcher
 *    l'application de démarrer.
 *  - **Rejeter la donnée invalide, garder le reste.** Un point aux
 *    coordonnées impossibles est ignoré, la trace qui le contient est
 *    conservée.
 */

import { decodePolyline6Validated, type PolylinePoint } from './polyline'
import { scanXml } from './xml'
import {
  LIMITS,
  ROUTING_PROFILES,
  createCustomMarker,
  isValidLat,
  isValidLon,
  normalizeMarkerType,
  type CustomMarker,
  type GpxSourceType,
  type RoutingProfileId,
  type TrackPoint,
  type TrackSegment,
  type WayInOut,
  type WayVia,
  type Waypoint,
} from './types'

/** Espace de noms des extensions, tel qu'écrit par l'application. */
export const VELOGPX_NAMESPACE = 'https://corentin.click/velogpx/v1'

export interface ParsedTrack {
  readonly name: string | null
  /**
   * `<name>` du `<trk>`, tel que le fichier le porte — `null` s'il n'en a pas.
   *
   * Distinct de `name`, qui reprend le nom de fichier quand il y en a un et
   * vaut donc **la même chose pour toutes les traces d'un même GPX**. C'est le
   * seul champ qui les distingue au dialogue de sélection.
   */
  readonly fileTrackName: string | null
  readonly segments: readonly TrackSegment[]
  readonly customMarkers: readonly CustomMarker[]
  readonly wayInOuts: readonly WayInOut[]
  /** Géométries embarquées, indexées par identifiant de way. */
  readonly wayRoutes: ReadonlyMap<string, readonly PolylinePoint[]>
  readonly realStartDistance: number
  readonly kmMarkersFromFictiveStart: boolean
  /**
   * Le fichier déclare-t-il cette trace comme un itinéraire routier calculé ?
   *
   * Une trace ainsi importée **n'a pas de tracé d'origine dans son fichier** :
   * ses `<trkseg>` sont déjà le résultat du calcul. « Revenir aux points
   * d'origine » n'aurait donc rien à relire, et c'est le recalcul qui lui est
   * proposé à la place.
   */
  readonly routed: boolean
  readonly routingProfile: RoutingProfileId | null
}

export interface ParseResult {
  readonly tracks: readonly ParsedTrack[]
  readonly waypoints: readonly Waypoint[]
  /** `RTE` si les points d'intérêt viennent d'une route, `WPT` sinon. */
  readonly waypointSourceType: GpxSourceType
}

/**
 * Nature d'une trace assemblée, telle que la fiche d'infos l'annonce.
 *
 * `waypointSourceType` ne qualifie que les **points d'intérêt** du fichier :
 * l'appliquer à une trace née d'un `<trk>` la faisait annoncer « WPT » alors
 * qu'elle vient bien d'une trace. Android le dit par sa construction — les
 * traces issues d'un `<trk>` sont bâties sans `sourceType` et gardent le
 * défaut `TRK`, et seule la trace de synthèse, créée quand le fichier n'a
 * aucun `<trk>`, reçoit `result.waypointSourceType` (`GpxParser.assembleTracks`).
 *
 * La règle vit ici plutôt qu'aux deux endroits qui assemblent : c'est leur
 * divergence qui avait produit le défaut, l'un des deux étant juste.
 */
export function assembledSourceType(
  fromTrackElement: boolean,
  waypointSourceType: GpxSourceType,
): GpxSourceType {
  return fromTrackElement ? 'TRK' : waypointSourceType
}

/** Tronque un texte sans transformer une absence de valeur en chaîne vide. */
function clamp(value: string | null): string | null {
  if (value === null) return null
  return value.length > LIMITS.MAX_TEXT_LENGTH
    ? value.slice(0, LIMITS.MAX_TEXT_LENGTH)
    : value
}

function parseCoordinate(
  attributes: ReadonlyMap<string, string>,
): { lat: number; lon: number } | null {
  const lat = Number.parseFloat(attributes.get('lat') ?? '')
  const lon = Number.parseFloat(attributes.get('lon') ?? '')
  return isValidLat(lat) && isValidLon(lon) ? { lat, lon } : null
}

function parseOptionalFloat(value: string | undefined): number | null {
  if (value === undefined) return null
  const parsed = Number.parseFloat(value)
  return Number.isFinite(parsed) ? parsed : null
}

/**
 * Booléen d'attribut, en mode strict.
 *
 * Une valeur inattendue est traitée comme absente et retombe sur le défaut,
 * plutôt que d'être interprétée comme « faux ».
 */
function parseStrictBoolean(value: string | undefined): boolean | null {
  if (value === 'true') return true
  if (value === 'false') return false
  return null
}

/** Nom local d'une balise, préfixe d'espace de noms retiré. */
function localName(name: string): string {
  const colon = name.indexOf(':')
  return colon === -1 ? name : name.slice(colon + 1)
}

/** Vrai pour une balise d'extension VeloGPX, quel que soit son préfixe. */
function isVeloGpxTag(name: string, expected: string): boolean {
  return name.includes(':') && localName(name) === expected
}

interface MarkerDraft {
  type: string | null
  symbol: string | null
  distanceKm: number | null
  visible: boolean
  name: string | null
  description: string | null
}

interface WayDraft {
  exitKm: number | null
  entryKm: number | null
  visible: boolean
  name: string | null
  vias: WayVia[]
  route: readonly PolylinePoint[] | null
}

/**
 * Analyse un document GPX.
 *
 * `fileName` sert de nom de trace prioritaire : les `<name>` des fichiers
 * d'organisateurs sont souvent génériques (« Nouveau fichier 1 »), alors que
 * le nom du fichier est presque toujours parlant.
 */
export function parseGpx(
  source: string,
  options: {
    fileName?: string | null
    defaultTrackName?: string
  } = {},
): ParseResult {
  const fileName = options.fileName ?? null
  const defaultTrackName = options.defaultTrackName ?? 'Track'

  const tracks: ParsedTrack[] = []
  const globalWaypoints: Waypoint[] = []
  let hasRte = false

  // --- État de la trace en cours ---
  let inTrack = false
  let currentTrackName: string | null = null
  let currentSegments: TrackSegment[] = []
  let currentPoints: TrackPoint[] = []
  let pointsInTrack = 0

  // --- État du point en cours ---
  let inTrackPoint = false
  let trkptLat = 0
  let trkptLon = 0
  let trkptEle: number | null = null
  let trkptTime: string | null = null
  let trkptName: string | null = null

  // --- Points d'intérêt ---
  let inWaypoint = false
  let inRoute = false
  let inRoutePoint = false
  let waypointLat = 0
  let waypointLon = 0
  let waypointEle: number | null = null
  let waypointName: string | null = null
  let waypointDesc: string | null = null
  let routePoints: Waypoint[] = []

  // --- Extensions VeloGPX ---
  let inExtensions = false
  let realStartDistance = 0
  let kmMarkersFromFictiveStart = false
  let customMarkers: CustomMarker[] = []
  let wayInOuts: WayInOut[] = []
  let wayRoutes = new Map<string, readonly PolylinePoint[]>()
  let markerDraft: MarkerDraft | null = null
  let wayDraft: WayDraft | null = null
  // Budget partagé par tout le fichier, et non par détour : c'est leur
  // composition qui produisait l'amplification (voir `LIMITS`).
  let wayRoutePointBudget = LIMITS.MAX_WAY_ROUTE_POINTS_PER_FILE
  let trackRouted = false
  let trackRoutingProfile: RoutingProfileId | null = null

  let text = ''

  const resetTrackExtensions = (): void => {
    realStartDistance = 0
    kmMarkersFromFictiveStart = false
    customMarkers = []
    wayInOuts = []
    wayRoutes = new Map()
    markerDraft = null
    wayDraft = null
    inExtensions = false
    trackRouted = false
    trackRoutingProfile = null
  }

  const finishSegment = (): void => {
    if (
      currentPoints.length > 0 &&
      currentSegments.length < LIMITS.MAX_SEGMENTS_PER_TRACK
    ) {
      currentSegments.push({ points: currentPoints })
    }
    currentPoints = []
  }

  for (const event of scanXml(source)) {
    if (event.kind === 'text') {
      text += event.text
      continue
    }

    if (event.kind === 'start') {
      const name = event.name
      const local = localName(name)
      text = ''

      switch (local) {
        case 'trk':
          if (!name.includes(':') || isVeloGpxTag(name, 'trk')) {
            inTrack = true
            currentTrackName = null
            currentSegments = []
            currentPoints = []
            pointsInTrack = 0
            resetTrackExtensions()
          }
          break

        case 'trkseg':
          currentPoints = []
          break

        case 'trkpt': {
          const coord = parseCoordinate(event.attributes)
          if (coord && pointsInTrack < LIMITS.MAX_POINTS_PER_TRACK) {
            inTrackPoint = true
            trkptLat = coord.lat
            trkptLon = coord.lon
            trkptEle = null
            trkptTime = null
            trkptName = null
          }
          break
        }

        case 'wpt': {
          const coord = parseCoordinate(event.attributes)
          if (coord && globalWaypoints.length < LIMITS.MAX_WAYPOINTS_PER_FILE) {
            inWaypoint = true
            waypointLat = coord.lat
            waypointLon = coord.lon
            waypointEle = null
            waypointName = null
            waypointDesc = null
          }
          break
        }

        case 'rte':
          inRoute = true
          hasRte = true
          routePoints = []
          break

        case 'rtept': {
          const coord = parseCoordinate(event.attributes)
          if (coord && routePoints.length < LIMITS.MAX_WAYPOINTS_PER_FILE) {
            inRoutePoint = true
            waypointLat = coord.lat
            waypointLon = coord.lon
            waypointEle = null
            waypointName = null
            waypointDesc = null
          }
          break
        }

        case 'extensions':
          // Seules les extensions portées par une trace sont interprétées.
          if (inTrack) inExtensions = true
          break

        case 'marker':
          if (
            inExtensions &&
            isVeloGpxTag(name, 'marker') &&
            customMarkers.length < LIMITS.MAX_CUSTOM_MARKERS_PER_TRACK
          ) {
            markerDraft = {
              type: event.attributes.get('type') ?? null,
              symbol: event.attributes.get('symbol') ?? null,
              distanceKm: parseOptionalFloat(event.attributes.get('distanceKm')),
              visible: parseStrictBoolean(event.attributes.get('visible')) ?? true,
              name: null,
              description: null,
            }
          }
          break

        case 'wayInOut':
          if (
            inExtensions &&
            isVeloGpxTag(name, 'wayInOut') &&
            wayInOuts.length < LIMITS.MAX_WAYS_PER_TRACK
          ) {
            wayDraft = {
              exitKm: parseOptionalFloat(event.attributes.get('exitKm')),
              entryKm: parseOptionalFloat(event.attributes.get('entryKm')),
              visible: parseStrictBoolean(event.attributes.get('visible')) ?? true,
              name: null,
              vias: [],
              route: null,
            }

            // Format historique à point de passage unique, porté par des
            // attributs sur la balise elle-même. Lu, mais plus jamais écrit.
            const legacyLat = parseOptionalFloat(event.attributes.get('viaLat'))
            const legacyLon = parseOptionalFloat(event.attributes.get('viaLon'))
            if (
              legacyLat !== null &&
              legacyLon !== null &&
              isValidLat(legacyLat) &&
              isValidLon(legacyLon)
            ) {
              wayDraft.vias.push({ lat: legacyLat, lon: legacyLon })
            }
          }
          break

        case 'via':
          if (
            wayDraft &&
            isVeloGpxTag(name, 'via') &&
            wayDraft.vias.length < LIMITS.MAX_VIAS_PER_WAY
          ) {
            const lat = parseOptionalFloat(event.attributes.get('lat'))
            const lon = parseOptionalFloat(event.attributes.get('lon'))
            // Un point de passage aberrant est ignoré, le détour survit.
            if (lat !== null && lon !== null && isValidLat(lat) && isValidLon(lon)) {
              wayDraft.vias.push({ lat, lon })
            }
          }
          break

        default:
          break
      }

      // Une balise auto-fermante n'aura pas d'événement de fin : on la traite
      // immédiatement pour que `<velogpx:via .../>` soit bien pris en compte.
      if (event.selfClosing) {
        closeElement(name)
      }

      continue
    }

    closeElement(event.name)
  }

  return {
    tracks,
    waypoints: globalWaypoints,
    waypointSourceType: hasRte ? 'RTE' : 'WPT',
  }

  function closeElement(name: string): void {
    const local = localName(name)
    const content = text
    text = ''

    switch (local) {
      case 'ele': {
        const value = Number.parseFloat(content.trim())
        if (Number.isFinite(value)) {
          if (inTrackPoint) trkptEle = value
          else if (inRoutePoint || inWaypoint) waypointEle = value
        }
        break
      }

      case 'time':
        // Conservé tel quel : aucune normalisation, l'export doit pouvoir
        // restituer exactement ce qui a été lu.
        if (inTrackPoint) trkptTime = clamp(content.trim())
        break

      case 'name': {
        const value = clamp(content.trim())
        if (isVeloGpxTag(name, 'name')) {
          // Les extensions conservent les espaces significatifs.
          const raw = clamp(content)
          if (markerDraft) markerDraft.name = raw
          else if (wayDraft) wayDraft.name = raw
        } else if (inRoutePoint) waypointName = value
        else if (inTrackPoint) trkptName = value
        else if (inWaypoint) waypointName = value
        else if (inTrack && !inExtensions) currentTrackName = value
        break
      }

      case 'desc':
        if (inRoutePoint || inWaypoint) waypointDesc = clamp(content.trim())
        break

      case 'description':
        if (isVeloGpxTag(name, 'description') && markerDraft) {
          markerDraft.description = clamp(content)
        }
        break

      case 'route':
        if (
          wayDraft &&
          isVeloGpxTag(name, 'route') &&
          content.length <= LIMITS.MAX_WAY_ROUTE_CHARS &&
          wayRoutePointBudget > 0
        ) {
          // Le budget est passé au décodeur pour que rien ne soit matérialisé
          // au-delà, puis décompté de ce qui a été décodé — y compris quand la
          // validation rejette la géométrie ensuite : la mémoire a bien été
          // occupée, et une polyline aberrante ne doit pas ouvrir un passage
          // gratuit.
          const decoded = decodePolyline6Validated(
            content.trim(),
            wayRoutePointBudget,
          )
          wayRoutePointBudget -= decoded?.length ?? 0
          wayDraft.route = decoded
        }
        break

      case 'trkpt':
        if (inTrackPoint) {
          currentPoints.push({
            lat: trkptLat,
            lon: trkptLon,
            ele: trkptEle,
            time: trkptTime,
            name: trkptName,
          })
          pointsInTrack++
          inTrackPoint = false
        }
        break

      case 'trkseg':
        finishSegment()
        break

      case 'wpt':
        if (inWaypoint) {
          globalWaypoints.push({
            lat: waypointLat,
            lon: waypointLon,
            ele: waypointEle,
            name: waypointName,
            desc: waypointDesc,
          })
          inWaypoint = false
        }
        break

      case 'rtept':
        if (inRoutePoint) {
          routePoints.push({
            lat: waypointLat,
            lon: waypointLon,
            ele: waypointEle,
            name: waypointName,
            desc: waypointDesc,
          })
          inRoutePoint = false
        }
        break

      case 'rte':
        if (inRoute) {
          for (const point of routePoints) {
            if (globalWaypoints.length >= LIMITS.MAX_WAYPOINTS_PER_FILE) break
            globalWaypoints.push(point)
          }
          routePoints = []
          inRoute = false
        }
        break

      case 'marker':
        if (markerDraft && isVeloGpxTag(name, 'marker')) {
          const draft = markerDraft
          if (
            draft.type !== null &&
            draft.name !== null &&
            draft.symbol !== null &&
            draft.distanceKm !== null &&
            draft.distanceKm >= 0
          ) {
            customMarkers.push(
              createCustomMarker({
                type: normalizeMarkerType(draft.type),
                name: draft.name,
                symbol: draft.symbol,
                distanceKm: draft.distanceKm,
                description: draft.description ?? '',
                visible: draft.visible,
              }),
            )
          }
          markerDraft = null
        }
        break

      case 'wayInOut':
        if (wayDraft && isVeloGpxTag(name, 'wayInOut')) {
          const draft = wayDraft
          if (
            draft.exitKm !== null &&
            draft.entryKm !== null &&
            draft.exitKm >= 0 &&
            draft.entryKm >= 0
          ) {
            // Identifiant régénéré : il ne survit pas au format GPX, la
            // correspondance avec les préférences se fait par géométrie.
            const id = generateWayId()
            wayInOuts.push({
              id,
              name: draft.name ?? '',
              exitKm: draft.exitKm,
              entryKm: draft.entryKm,
              vias: draft.vias,
              visible: draft.visible,
            })
            if (draft.route) {
              wayRoutes.set(id, draft.route)
            }
          }
          wayDraft = null
        }
        break

      case 'extensions':
        inExtensions = false
        break

      case 'trk':
        if (inTrack) {
          finishSegment()

          const hasGeometry = currentSegments.some((s) => s.points.length > 0)
          if (hasGeometry && tracks.length < LIMITS.MAX_TRACKS_PER_FILE) {
            tracks.push({
              name:
                clamp(fileName) ??
                currentTrackName ??
                `${defaultTrackName} ${tracks.length + 1}`,
              fileTrackName: currentTrackName,
              segments: currentSegments,
              customMarkers,
              wayInOuts,
              wayRoutes,
              realStartDistance,
              kmMarkersFromFictiveStart,
              routed: trackRouted,
              routingProfile: trackRoutingProfile,
            })
          }

          inTrack = false
          currentSegments = []
          resetTrackExtensions()
        }
        break

      case 'realStartDistance':
        if (isVeloGpxTag(name, 'realStartDistance')) {
          const value = Number.parseFloat(content.trim())
          if (Number.isFinite(value) && value >= 0) realStartDistance = value
        }
        break

      case 'kmMarkersFromFictiveStart':
        if (isVeloGpxTag(name, 'kmMarkersFromFictiveStart')) {
          kmMarkersFromFictiveStart = content.trim() === 'true'
        }
        break

      case 'routed':
        if (isVeloGpxTag(name, 'routed')) {
          trackRouted = content.trim() === 'true'
        }
        break

      case 'routingProfile':
        if (isVeloGpxTag(name, 'routingProfile')) {
          const value = content.trim()
          // Un profil inconnu n'est pas retenu : le repli sur `CAR` se décide
          // à la construction de la trace, pas ici, pour que l'absence de
          // l'extension et une valeur illisible se distinguent encore.
          trackRoutingProfile =
            value in ROUTING_PROFILES ? (value as RoutingProfileId) : null
        }
        break

      default:
        break
    }
  }
}

let wayIdCounter = 0

/** Identifiant local d'un way, valide comme clé de cache. */
function generateWayId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  wayIdCounter++
  return `way-${Date.now().toString(36)}-${wayIdCounter}`
}

/**
 * Le nom de fichier désigne-t-il un GPX ?
 *
 * L'extension est le seul critère fiable côté navigateur : le type MIME
 * dépend du système et arrive souvent vide ou générique — `application/xml`,
 * voire `application/octet-stream` — depuis un fournisseur de stockage en
 * ligne, alors que le fichier est bien un GPX.
 */
export function isGpxFileName(name: string): boolean {
  return /\.gpx$/i.test(name.trim())
}

/** L'élément racine, éventuellement préfixé par un espace de noms. */
const GPX_ROOT = /^<([A-Za-z_][\w.-]*:)?gpx(\s|>|\/)/

/**
 * Nombre d'octets à lire pour reconnaître un GPX.
 *
 * Assez pour franchir un prologue, un DOCTYPE et des commentaires ; assez peu
 * pour qu'un fichier de plusieurs gigaoctets renommé `.gpx` soit écarté sans
 * jamais être chargé en mémoire. C'est ce plafond, et non une limite de
 * taille, qui protège l'onglet d'une sélection absurde.
 */
export const GPX_SNIFF_BYTES = 64 * 1024

/**
 * Le contenu est-il celui d'un GPX ?
 *
 * L'extension ne prouve rien — iOS laisse choisir n'importe quel fichier,
 * puisque son sélecteur ne sait pas filtrer les GPX — et l'analyse, elle, ne
 * lève pas : `parseGpx` tronque plutôt que d'échouer. Un fichier étranger
 * renommé `.gpx` passait donc les deux, pour finir en trace vide importée
 * sans un mot.
 *
 * Seul le **premier élément ouvert** fait foi : prologue, instructions de
 * traitement, commentaires et DOCTYPE peuvent le précéder et sont traversés.
 *
 * @param head Les premiers `GPX_SNIFF_BYTES` du fichier, décodés en texte.
 */
export function looksLikeGpx(head: string): boolean {
  // Un binaire décodé en UTF-8 garde ses octets nuls, qu'aucun XML ne porte :
  // c'est le signe le plus sûr, et le moins cher, d'un fichier qui n'est pas
  // du texte.
  if (head.includes('\u0000')) return false

  let rest = head.replace(/^\uFEFF/, '')

  for (;;) {
    const before = rest
    rest = rest.trimStart()

    if (rest.startsWith('<?')) {
      const end = rest.indexOf('?>')
      if (end < 0) return false
      rest = rest.slice(end + 2)
    } else if (rest.startsWith('<!--')) {
      const end = rest.indexOf('-->')
      if (end < 0) return false
      rest = rest.slice(end + 3)
    } else if (/^<!DOCTYPE/i.test(rest)) {
      // Un sous-ensemble interne — `<!DOCTYPE gpx [ ... ]>` — contiendrait des
      // `>` avant la fin de la déclaration et ferait échouer cette lecture. Le
      // cas est écarté sciemment : aucun producteur de GPX n'en écrit, et le
      // reconnaître demanderait un analyseur de DTD pour rien.
      const end = rest.indexOf('>')
      if (end < 0) return false
      rest = rest.slice(end + 1)
    }

    if (rest === before) break
  }

  return GPX_ROOT.test(rest)
}
